import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { Button as ShadcnButton, buttonVariants } from './shadcn/button'

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost'
type Size = 'sm' | 'md'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  isLoading?: boolean
}

// This app's own variant/size names, mapped onto shadcn/ui's actual
// button.tsx variants (default/outline/destructive/ghost) rather than
// renaming every call site across the app to shadcn's vocabulary.
const shadcnVariant: Record<Variant, 'default' | 'outline' | 'destructive' | 'ghost'> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  ghost: 'ghost',
}

const shadcnSize: Record<Size, 'default' | 'lg'> = {
  sm: 'default',
  md: 'lg',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', isLoading, className = '', children, disabled, ...props },
  ref,
) {
  return (
    <ShadcnButton
      ref={ref}
      variant={shadcnVariant[variant]}
      size={shadcnSize[size]}
      disabled={disabled || isLoading}
      className={cn('max-md:min-h-11', className)}
      {...props}
    >
      {isLoading && (
        <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {children}
    </ShadcnButton>
  )
})

interface ButtonLinkProps extends LinkProps {
  variant?: Variant
  size?: Size
}

// A Button that navigates via react-router instead of submitting/onClick --
// renders an <a>, never a <button>, so it's never invalid nested-interactive
// markup like <a><button/></a>. Same visual variants/sizes as Button, built
// from the same shadcn buttonVariants() so the two never drift apart.
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...props
}: ButtonLinkProps) {
  return (
    <Link
      className={cn(buttonVariants({ variant: shadcnVariant[variant], size: shadcnSize[size] }), "max-md:min-h-11", className)}
      {...props}
    >
      {children}
    </Link>
  )
}
